import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Wrench, Plus, Pencil, Trash2, CheckCircle, AlertTriangle, Truck, Package, Satellite, Construction } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIGGERS = [
  { value: "days", label: "Days" },
  { value: "km", label: "Kilometres" },
  { value: "hours", label: "Hours" },
  { value: "both", label: "Multiple (any-of)" },
];

const blankSched = () => ({
  asset_kind: "vehicle", asset_id: "",
  name: "", description: "",
  trigger_type: "days", cadence_days: 90, cadence_km: null, cadence_hours: null,
  last_serviced_at: "", last_service_odometer: null, last_service_hours: null,
  notify_admin: true, notify_assigned_employee: false, reminder_lead_days: 14,
  is_active: true, notes: "",
});

const blankComplete = () => ({
  service_date: new Date().toISOString().slice(0, 10),
  odometer: null, hours: null, cost: null, vendor: "", notes: "",
});

export default function FleetAssetTracker() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [gps, setGps] = useState(null);
  const [schedDialog, setSchedDialog] = useState(null);
  const [completeDialog, setCompleteDialog] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [ov, sc, v, e, g] = await Promise.all([
        axios.get(`${API}/fleet/overview`),
        axios.get(`${API}/maintenance-schedules?include_inactive=true`),
        axios.get(`${API}/fleet/vehicles`).catch(() => ({ data: [] })),
        axios.get(`${API}/equipment`).catch(() => ({ data: [] })),
        axios.get(`${API}/fleet/gps-config`),
      ]);
      setOverview(ov.data);
      setSchedules(sc.data || []);
      setVehicles(v.data?.items || v.data || []);
      setEquipment(e.data?.items || e.data || []);
      setGps(g.data);
    } catch (err) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const filteredScheds = useMemo(() => {
    if (filter === "overdue") return schedules.filter((s) => s.is_overdue);
    if (filter === "due_soon") return schedules.filter((s) => !s.is_overdue && s.is_due_soon);
    if (filter === "vehicles") return schedules.filter((s) => s.asset_kind === "vehicle");
    if (filter === "equipment") return schedules.filter((s) => s.asset_kind === "equipment");
    return schedules;
  }, [schedules, filter]);

  const saveSched = async () => {
    if (!schedDialog.name || !schedDialog.asset_id) { toast.error("Pick asset + name"); return; }
    try {
      if (schedDialog.id) await axios.put(`${API}/maintenance-schedules/${schedDialog.id}`, schedDialog);
      else await axios.post(`${API}/maintenance-schedules`, schedDialog);
      toast.success("Saved"); setSchedDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deleteSched = async (id) => {
    if (!window.confirm("Delete schedule?")) return;
    try { await axios.delete(`${API}/maintenance-schedules/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  const doComplete = async () => {
    if (!completeDialog) return;
    try {
      await axios.post(`${API}/maintenance-schedules/${completeDialog.sched_id}/complete`, completeDialog.body);
      toast.success("Marked complete + next due recomputed");
      setCompleteDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveGps = async () => {
    try {
      const r = await axios.put(`${API}/fleet/gps-config`, gps);
      setGps(r.data);
      toast.success("Registered interest — we'll be in touch when Track B launches.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const assetName = (kind, id) => {
    const pool = kind === "vehicle" ? vehicles : equipment;
    const x = pool.find((a) => a.id === id);
    return x ? x.name : "(deleted)";
  };

  return (
    <div className="space-y-6 animate-in" data-testid="fleet-asset-tracker-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><Truck className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Fleet & Asset Tracker</h1>
          <p className="text-muted-foreground">Recurring maintenance schedules across vehicles + equipment.</p>
        </div>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="overview-cards">
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Vehicles</p><p className="text-2xl font-bold flex items-center gap-1"><Truck className="h-5 w-5" /> {overview.vehicle_count}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Equipment</p><p className="text-2xl font-bold flex items-center gap-1"><Package className="h-5 w-5" /> {overview.equipment_count}</p></CardContent></Card>
          <Card className={overview.overdue_count > 0 ? "border-destructive" : ""}><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Overdue</p><p className={`text-2xl font-bold ${overview.overdue_count > 0 ? "text-destructive" : ""}`}>{overview.overdue_count}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Due in lead window</p><p className="text-2xl font-bold text-amber-700">{overview.due_within_lead_count}</p></CardContent></Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-lg">
          <TabsTrigger value="overview" data-testid="tab-overview"><Wrench className="h-4 w-4 mr-1" />Schedules</TabsTrigger>
          <TabsTrigger value="expiring" data-testid="tab-expiring"><AlertTriangle className="h-4 w-4 mr-1" />Insurance / Reg</TabsTrigger>
          <TabsTrigger value="gps" data-testid="tab-gps"><Satellite className="h-4 w-4 mr-1" />Track B (GPS)</TabsTrigger>
        </TabsList>

        {/* SCHEDULES */}
        <TabsContent value="overview">
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <div><CardTitle>Maintenance schedules</CardTitle><CardDescription>Hooked into the reminder engine so admins + assigned employees get pinged ahead of time.</CardDescription></div>
              <div className="flex gap-2 items-center">
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-44" data-testid="filter-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ({schedules.length})</SelectItem>
                    <SelectItem value="overdue">Overdue only</SelectItem>
                    <SelectItem value="due_soon">Due soon</SelectItem>
                    <SelectItem value="vehicles">Vehicles only</SelectItem>
                    <SelectItem value="equipment">Equipment only</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => setSchedDialog(blankSched())} data-testid="new-sched-btn"><Plus className="h-4 w-4 mr-1" /> New schedule</Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Asset</TableHead><TableHead>Task</TableHead><TableHead>Trigger</TableHead><TableHead>Next due</TableHead><TableHead>Status</TableHead><TableHead className="text-right" />
                </TableRow></TableHeader>
                <TableBody>
                  {filteredScheds.map((s) => (
                    <TableRow key={s.id} className={!s.is_active ? "opacity-50" : s.is_overdue ? "bg-destructive/5" : s.is_due_soon ? "bg-amber-50/50" : ""} data-testid={`sched-row-${s.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {s.asset_kind === "vehicle" ? <Truck className="h-3.5 w-3.5" /> : <Package className="h-3.5 w-3.5" />}
                          <span className="text-sm">{assetName(s.asset_kind, s.asset_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-xs">
                        {s.cadence_days && <Badge variant="outline" className="mr-1">{s.cadence_days}d</Badge>}
                        {s.cadence_km && <Badge variant="outline" className="mr-1">{s.cadence_km}km</Badge>}
                        {s.cadence_hours && <Badge variant="outline">{s.cadence_hours}h</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{s.next_due_at || "—"}{s.next_due_odometer && <span className="block">@{s.next_due_odometer}km</span>}</TableCell>
                      <TableCell>
                        {s.is_overdue ? <Badge variant="destructive">Overdue · {Math.abs(s.days_until_due)}d</Badge>
                          : s.is_due_soon ? <Badge className="bg-amber-100 text-amber-700 border-amber-300" variant="outline">Due in {s.days_until_due}d</Badge>
                          : s.is_active ? <Badge variant="outline">OK</Badge>
                          : <Badge variant="secondary">Inactive</Badge>}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setCompleteDialog({ sched_id: s.id, body: blankComplete() })} data-testid={`complete-${s.id}`}><CheckCircle className="h-3.5 w-3.5 text-emerald-600" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setSchedDialog({ ...s })} data-testid={`edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteSched(s.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredScheds.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No schedules in this filter.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* EXPIRING DOCS */}
        <TabsContent value="expiring">
          <Card>
            <CardHeader><CardTitle>Vehicle docs expiring within 60 days</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Card className={overview?.insurance_expiring_60d > 0 ? "border-amber-300" : ""}><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Insurance expiring</p><p className="text-3xl font-bold">{overview?.insurance_expiring_60d ?? 0}</p></CardContent></Card>
                <Card className={overview?.registration_expiring_60d > 0 ? "border-amber-300" : ""}><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Registration expiring</p><p className="text-3xl font-bold">{overview?.registration_expiring_60d ?? 0}</p></CardContent></Card>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Edit insurance + registration dates directly on each vehicle&apos;s record (Fleet → Vehicles).</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GPS TRACK B */}
        <TabsContent value="gps">
          <Card className="border-dashed">
            <CardHeader className="flex flex-row items-center gap-3">
              <div className="p-3 rounded-lg bg-amber-100"><Construction className="h-6 w-6 text-amber-700" /></div>
              <div>
                <CardTitle>Track B — Live GPS Hardware</CardTitle>
                <CardDescription>Live vehicle telemetry from GPS dongles (Geotab / Samsara / etc). <b>Under construction</b> — register your interest below.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex justify-between items-center cursor-pointer">
                <span>I&apos;m interested in Track B when it launches</span>
                <Switch checked={gps?.interest_registered || false} onCheckedChange={(v) => setGps({ ...gps, interest_registered: v })} data-testid="gps-interest-switch" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Preferred provider</Label>
                  <Select value={gps?.preferred_provider || "_blank"} onValueChange={(v) => setGps({ ...gps, preferred_provider: v === "_blank" ? null : v })}>
                    <SelectTrigger data-testid="gps-provider-select"><SelectValue placeholder="Not sure yet" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_blank">Not sure yet</SelectItem>
                      <SelectItem value="geotab">Geotab</SelectItem>
                      <SelectItem value="samsara">Samsara</SelectItem>
                      <SelectItem value="azuga">Azuga</SelectItem>
                      <SelectItem value="custom">Custom / Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Estimated vehicles to track</Label><Input type="number" min="0" value={gps?.estimated_vehicles ?? ""} onChange={(e) => setGps({ ...gps, estimated_vehicles: e.target.value === "" ? null : parseInt(e.target.value) })} data-testid="gps-vehicles-input" /></div>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={gps?.notes || ""} onChange={(e) => setGps({ ...gps, notes: e.target.value })} /></div>
              <Button onClick={saveGps} data-testid="save-gps-btn">Register interest</Button>
              {gps?.requested_at && <p className="text-xs text-muted-foreground">Interest registered on {new Date(gps.requested_at).toLocaleString()}. We&apos;ll reach out when Track B is live.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Schedule editor */}
      <Dialog open={!!schedDialog} onOpenChange={(o) => !o && setSchedDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{schedDialog?.id ? "Edit schedule" : "New maintenance schedule"}</DialogTitle></DialogHeader>
          {schedDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Asset kind</Label>
                  <Select value={schedDialog.asset_kind} onValueChange={(v) => setSchedDialog({ ...schedDialog, asset_kind: v, asset_id: "" })}>
                    <SelectTrigger data-testid="sched-asset-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vehicle">Vehicle</SelectItem>
                      <SelectItem value="equipment">Equipment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Asset</Label>
                  <Select value={schedDialog.asset_id} onValueChange={(v) => setSchedDialog({ ...schedDialog, asset_id: v })}>
                    <SelectTrigger data-testid="sched-asset-id"><SelectValue placeholder="Pick asset" /></SelectTrigger>
                    <SelectContent>
                      {(schedDialog.asset_kind === "vehicle" ? vehicles : equipment).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Task name</Label><Input value={schedDialog.name} onChange={(e) => setSchedDialog({ ...schedDialog, name: e.target.value })} data-testid="sched-name-input" /></div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label>Trigger</Label>
                  <Select value={schedDialog.trigger_type} onValueChange={(v) => setSchedDialog({ ...schedDialog, trigger_type: v })}>
                    <SelectTrigger data-testid="sched-trigger-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Days</Label><Input type="number" value={schedDialog.cadence_days ?? ""} onChange={(e) => setSchedDialog({ ...schedDialog, cadence_days: e.target.value === "" ? null : parseInt(e.target.value) })} data-testid="cadence-days-input" /></div>
                <div><Label>KM</Label><Input type="number" value={schedDialog.cadence_km ?? ""} onChange={(e) => setSchedDialog({ ...schedDialog, cadence_km: e.target.value === "" ? null : parseInt(e.target.value) })} /></div>
                <div><Label>Hours</Label><Input type="number" value={schedDialog.cadence_hours ?? ""} onChange={(e) => setSchedDialog({ ...schedDialog, cadence_hours: e.target.value === "" ? null : parseInt(e.target.value) })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Last serviced</Label><Input type="date" value={schedDialog.last_serviced_at || ""} onChange={(e) => setSchedDialog({ ...schedDialog, last_serviced_at: e.target.value })} /></div>
                <div><Label>Last odometer</Label><Input type="number" value={schedDialog.last_service_odometer ?? ""} onChange={(e) => setSchedDialog({ ...schedDialog, last_service_odometer: e.target.value === "" ? null : parseInt(e.target.value) })} /></div>
                <div><Label>Reminder lead (days)</Label><Input type="number" value={schedDialog.reminder_lead_days} onChange={(e) => setSchedDialog({ ...schedDialog, reminder_lead_days: parseInt(e.target.value || 14) })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={schedDialog.notify_admin} onCheckedChange={(v) => setSchedDialog({ ...schedDialog, notify_admin: v })} /> Notify admin</label>
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={schedDialog.notify_assigned_employee} onCheckedChange={(v) => setSchedDialog({ ...schedDialog, notify_assigned_employee: v })} /> Notify assigned employee</label>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={schedDialog.notes || ""} onChange={(e) => setSchedDialog({ ...schedDialog, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedDialog(null)}>Cancel</Button>
            <Button onClick={saveSched} data-testid="save-sched-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={!!completeDialog} onOpenChange={(o) => !o && setCompleteDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark complete</DialogTitle><DialogDescription>Logs the service + recomputes next-due window.</DialogDescription></DialogHeader>
          {completeDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Service date</Label><Input type="date" value={completeDialog.body.service_date} onChange={(e) => setCompleteDialog({ ...completeDialog, body: { ...completeDialog.body, service_date: e.target.value } })} data-testid="complete-date-input" /></div>
                <div><Label>Cost ($)</Label><Input type="number" step="0.01" value={completeDialog.body.cost ?? ""} onChange={(e) => setCompleteDialog({ ...completeDialog, body: { ...completeDialog.body, cost: e.target.value === "" ? null : parseFloat(e.target.value) } })} /></div>
                <div><Label>Odometer (if km-based)</Label><Input type="number" value={completeDialog.body.odometer ?? ""} onChange={(e) => setCompleteDialog({ ...completeDialog, body: { ...completeDialog.body, odometer: e.target.value === "" ? null : parseInt(e.target.value) } })} data-testid="complete-odometer-input" /></div>
                <div><Label>Hours (if hr-based)</Label><Input type="number" value={completeDialog.body.hours ?? ""} onChange={(e) => setCompleteDialog({ ...completeDialog, body: { ...completeDialog.body, hours: e.target.value === "" ? null : parseInt(e.target.value) } })} /></div>
                <div className="col-span-2"><Label>Vendor</Label><Input value={completeDialog.body.vendor || ""} onChange={(e) => setCompleteDialog({ ...completeDialog, body: { ...completeDialog.body, vendor: e.target.value } })} /></div>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={completeDialog.body.notes || ""} onChange={(e) => setCompleteDialog({ ...completeDialog, body: { ...completeDialog.body, notes: e.target.value } })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialog(null)}>Cancel</Button>
            <Button onClick={doComplete} data-testid="confirm-complete-btn">Mark complete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
